'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { User } from 'firebase/auth';
import { firebaseEnabled } from '@/lib/firebase-config';
import { runWhenIdleOrInteraction } from '@/lib/defer-until-idle';
import type { AiCreditSummary, ToolCreditSummary } from '@/lib/tool-access';

// firebase/auth + firebase-client (which initializes the Firebase app) are ~140 KB
// and were previously imported statically into this site-wide provider, landing in
// the initial bundle on every page (blog/reference included) even though auth is only
// used in account flows. Load them lazily after hydration / on demand instead, so the
// SDK is a separate async chunk off the critical render path (better mobile LCP/TBT).
type FirebaseAuthModule = typeof import('firebase/auth');
type FirebaseClientModule = typeof import('@/lib/firebase-client');
type FirebaseBundle = { auth: FirebaseAuthModule; client: FirebaseClientModule };

let firebasePromise: Promise<FirebaseBundle> | null = null;
function loadFirebase(): Promise<FirebaseBundle> {
  if (!firebasePromise) {
    firebasePromise = Promise.all([import('firebase/auth'), import('@/lib/firebase-client')]).then(
      ([auth, client]) => ({ auth, client }),
    );
  }
  return firebasePromise;
}

const ACCOUNT_SESSION_CACHE_KEY = 'cleanor-account-session';

type AccountSession = {
  identityType: 'guest' | 'user';
  account: {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoUrl: string | null;
    plan: 'free' | 'go' | 'pro' | 'business';
    tier: 'free' | 'go' | 'pro' | 'business';
    browserPlan: 'free' | 'go' | 'pro' | 'business';
    aiPlan: 'free' | 'go' | 'pro' | 'business';
    canUseAi: boolean;
    canUseApi: boolean;
    role: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    lastSeenAt: string;
    emailVerified: boolean;
    signInProvider: string | null;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    stripePriceId: string | null;
    billingStatus: string | null;
    currentPeriodEnd: string | null;
  } | null;
  browserCredits: ToolCreditSummary;
  aiCredits: AiCreditSummary;
  credits: ToolCreditSummary;
  pricing: {
    goMonthlyPriceUsd: number;
    goYearlyPriceUsd: number;
    goLookupKey: string;
    proMonthlyPriceUsd: number;
    proYearlyPriceUsd: number;
    businessMonthlyPriceUsd: number;
    proLookupKey: string;
  };
};

type AuthContextValue = {
  user: User | null;
  isLoading: boolean;
  session: AccountSession | null;
  isSessionLoading: boolean;
  errorMessage: string | null;
  firebaseEnabled: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<boolean>;
  signUpWithEmail: (email: string, password: string) => Promise<boolean>;
  sendPasswordReset: (email: string) => Promise<boolean>;
  signOutUser: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

function readCachedSession(): AccountSession | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(ACCOUNT_SESSION_CACHE_KEY);

    if (!rawValue) {
      return null;
    }

    return JSON.parse(rawValue) as AccountSession;
  } catch {
    return null;
  }
}

function writeCachedSession(session: AccountSession | null) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!session) {
    window.sessionStorage.removeItem(ACCOUNT_SESSION_CACHE_KEY);
    return;
  }

  window.sessionStorage.setItem(ACCOUNT_SESSION_CACHE_KEY, JSON.stringify(session));
}

const AuthContext = createContext<AuthContextValue | null>(null);

function getAuthErrorCode(error: unknown) {
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') {
    return error.code;
  }

  if (error instanceof Error) {
    const matchedCode = error.message.match(/auth\/[a-z-]+/i);

    if (matchedCode) {
      return matchedCode[0];
    }
  }

  return null;
}

function getReadableAuthError(
  error: unknown,
  providerName?: 'Google' | 'Meta' | 'Apple' | 'Email',
) {
  if (!(error instanceof Error)) {
    return 'Sign-in did not complete. Please try again.';
  }

  const code = getAuthErrorCode(error);
  const message = error.message;

  if (code === 'auth/configuration-not-found' || message.includes('auth/configuration-not-found')) {
    if (providerName === 'Apple') {
      return 'Apple sign-in is wired in the site, but the Apple provider is not fully configured in Firebase Auth yet.';
    }

    if (providerName === 'Meta') {
      return 'Meta sign-in is wired in the site, but the Facebook provider is not fully configured in Firebase Auth yet.';
    }

    return 'Firebase Auth is connected, but sign-in methods have not been enabled in the Firebase project yet.';
  }

  if (code === 'auth/operation-not-allowed' || message.includes('auth/operation-not-allowed')) {
    if (providerName === 'Apple') {
      return 'Apple sign-in is not enabled in Firebase Auth yet, or Apple code flow is not configured in Apple Developer.';
    }

    if (providerName === 'Meta') {
      return 'Meta sign-in is not enabled in Firebase Auth yet, or the Meta app settings are incomplete.';
    }

    if (providerName === 'Email') {
      return 'Email/password sign-in is not enabled in Firebase Auth yet.';
    }

    return 'This sign-in method is not enabled in Firebase Auth yet.';
  }

  if (code === 'auth/unauthorized-domain' || message.includes('auth/unauthorized-domain')) {
    return 'This domain is not yet listed as an authorized sign-in domain in Firebase Auth.';
  }

  if (
    code === 'auth/operation-not-supported-in-this-environment' ||
    message.includes('auth/operation-not-supported-in-this-environment')
  ) {
    return 'This browser cannot complete popup sign-in. Try again and the site should fall back to a redirect-based flow.';
  }

  if (code === 'auth/popup-closed-by-user' || message.includes('auth/popup-closed-by-user')) {
    return 'The sign-in popup was closed before the sign-in completed.';
  }

  if (code === 'auth/popup-blocked' || message.includes('auth/popup-blocked')) {
    return 'The browser blocked the sign-in popup. Try again after allowing popups for this site.';
  }

  if (
    code === 'auth/account-exists-with-different-credential' ||
    message.includes('auth/account-exists-with-different-credential')
  ) {
    return 'This email is already linked to a different sign-in method. Try the provider you used first.';
  }

  if (
    code === 'auth/invalid-credential' ||
    code === 'auth/invalid-login-credentials' ||
    message.includes('auth/invalid-credential') ||
    message.includes('auth/invalid-login-credentials')
  ) {
    return 'The email or password is incorrect.';
  }

  if (code === 'auth/invalid-email' || message.includes('auth/invalid-email')) {
    return 'Enter a valid email address.';
  }

  if (code === 'auth/missing-password' || message.includes('auth/missing-password')) {
    return 'Enter your password to continue.';
  }

  if (code === 'auth/weak-password' || message.includes('auth/weak-password')) {
    return 'Use a stronger password with at least 6 characters.';
  }

  if (code === 'auth/email-already-in-use' || message.includes('auth/email-already-in-use')) {
    return 'This email already has an account. Sign in instead, or reset the password.';
  }

  if (code === 'auth/user-not-found' || message.includes('auth/user-not-found')) {
    return 'No account was found for that email.';
  }

  if (code === 'auth/user-disabled' || message.includes('auth/user-disabled')) {
    return 'This account has been disabled in Firebase Auth.';
  }

  if (code === 'auth/too-many-requests' || message.includes('auth/too-many-requests')) {
    return 'Too many sign-in attempts. Wait a bit and try again.';
  }

  if (code === 'auth/cancelled-popup-request' || message.includes('auth/cancelled-popup-request')) {
    return 'Another sign-in popup was already open. Close it and try again.';
  }

  return 'Sign-in did not complete. Please try again.';
}

type ProviderKey = 'google' | 'facebook' | 'apple';
const PROVIDER_LABEL: Record<ProviderKey, 'Google' | 'Meta' | 'Apple'> = {
  google: 'Google',
  facebook: 'Meta',
  apple: 'Apple',
};

function resolveProvider(client: FirebaseClientModule, key: ProviderKey) {
  if (key === 'google') return client.firebaseGoogleProvider;
  if (key === 'facebook') return client.firebaseFacebookProvider;
  return client.firebaseAppleProvider;
}

/** True only if Firebase has a session stored for this browser, i.e. someone signed in. */
function hasPersistedFirebaseSession(): boolean {
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('firebase:authUser:')) {
        return true;
      }
    }
  } catch {
    /* storage blocked: treat as signed out and load on demand */
  }
  return false;
}

export default function FirebaseAuthProvider({ children }: { children: React.ReactNode }) {
  const cachedSession = readCachedSession();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<AccountSession | null>(cachedSession);
  const [isSessionLoading, setIsSessionLoading] = useState(!cachedSession);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const subscribedRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | undefined>(undefined);

  // Loads Firebase and subscribes to auth state, at most once. Every path that can
  // produce a signed-in user (a sign-in call, a redirect result) goes through this, so
  // the subscription always exists by the time there is a user to report.
  const ensureAuthSubscription = useCallback(async () => {
    if (!firebaseEnabled || subscribedRef.current) {
      return;
    }
    subscribedRef.current = true;

    const { auth, client } = await loadFirebase();
    if (!client.firebaseAuth) {
      setIsLoading(false);
      return;
    }

    unsubscribeRef.current = auth.onAuthStateChanged(client.firebaseAuth, (nextUser) => {
      setUser(nextUser);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!firebaseEnabled) {
      setIsLoading(false);
      return undefined;
    }

    // Firebase Auth (~140 KB) plus its cross-origin auth iframe (~93 KB) render nothing
    // on a tool page. A signed-out visitor never needs them at all, and signed-out
    // visitors are the overwhelming majority, and the ones Core Web Vitals is measured
    // on. Firebase stores a restored session under a `firebase:authUser:` key, so when
    // there is no such key there is nobody to restore: load nothing. A sign-in action
    // calls ensureAuthSubscription() itself, which pulls the SDK in on demand.
    if (!hasPersistedFirebaseSession()) {
      setIsLoading(false);
      return undefined;
    }

    // There IS a session to restore, so load it, but only once the browser is idle or
    // the user interacts, rather than competing with first paint.
    const cancelDeferred = runWhenIdleOrInteraction(() => void ensureAuthSubscription(), {
      timeout: 2500,
    });

    return () => {
      cancelDeferred();
      unsubscribeRef.current?.();
    };
  }, [ensureAuthSubscription]);

  const refreshSession = useCallback(async (options?: { background?: boolean }) => {
    if (!options?.background) {
      setIsSessionLoading(true);
    }

    setErrorMessage(null);

    try {
      const headers: HeadersInit = {};

      // Only reach for Firebase when there is actually a session to attach. Calling
      // loadFirebase() here unconditionally pulled the whole SDK for signed-out
      // visitors, which is exactly what the lazy subscription above exists to avoid.
      // A guest gets a guest session from the API, with no token and no SDK.
      if (firebaseEnabled && subscribedRef.current) {
        const { client } = await loadFirebase();
        if (client.firebaseAuth?.currentUser) {
          const idToken = await client.firebaseAuth.currentUser.getIdToken();
          headers.authorization = `Bearer ${idToken}`;
        }
      }

      const response = await fetch('/api/account/session', {
        method: 'GET',
        headers,
      });
      const payload = (await response.json()) as {
        success?: boolean;
        data?: AccountSession;
        error?: unknown;
      };

      if (!response.ok || !payload.success || !payload.data) {
        throw new Error(
          typeof payload.error === 'string' ? payload.error : 'Could not load account session.',
        );
      }

      setSession(payload.data);
      writeCachedSession(payload.data);
    } catch (error) {
      if (!options?.background) {
        setSession(null);
        writeCachedSession(null);
      }

      setErrorMessage(getReadableAuthError(error));
    } finally {
      setIsSessionLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const sessionUid = session?.account?.uid ?? null;
    const needsForegroundRefresh = Boolean(user && sessionUid && sessionUid !== user.uid);

    if (needsForegroundRefresh) {
      setSession(null);
      writeCachedSession(null);
      void refreshSession();
      return;
    }

    void refreshSession({ background: Boolean(session) });
  }, [isLoading, refreshSession, user]);

  const signInWithProvider = useCallback(
    async (
      providerKey: ProviderKey,
      fallbackMessage: string,
      options?: { preferRedirect?: boolean },
    ) => {
      if (!firebaseEnabled) {
        setErrorMessage(fallbackMessage);
        return;
      }

      setErrorMessage(null);

      // The subscription is lazy for signed-out visitors, so establish it before signing
      // in: setUser only ever fires from onAuthStateChanged.
      await ensureAuthSubscription();

      let bundle: FirebaseBundle;
      try {
        bundle = await loadFirebase();
      } catch {
        setErrorMessage(fallbackMessage);
        return;
      }

      const { auth, client } = bundle;
      const provider = resolveProvider(client, providerKey);

      if (!client.firebaseAuth || !provider) {
        setErrorMessage(fallbackMessage);
        return;
      }

      try {
        if (options?.preferRedirect) {
          await auth.signInWithRedirect(client.firebaseAuth, provider);
          return;
        }

        await auth.signInWithPopup(client.firebaseAuth, provider);
      } catch (error) {
        const code = getAuthErrorCode(error);

        if (
          code === 'auth/popup-blocked' ||
          code === 'auth/operation-not-supported-in-this-environment'
        ) {
          await auth.signInWithRedirect(client.firebaseAuth, provider);
          return;
        }

        setErrorMessage(getReadableAuthError(error, PROVIDER_LABEL[providerKey]));
      }
    },
    [],
  );

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      if (!firebaseEnabled) {
        setErrorMessage('Email/password sign-in is not configured on this site yet.');
        return false;
      }

      setErrorMessage(null);
      await ensureAuthSubscription();

      try {
        const { auth, client } = await loadFirebase();
        if (!client.firebaseAuth) {
          setErrorMessage('Email/password sign-in is not configured on this site yet.');
          return false;
        }
        await auth.signInWithEmailAndPassword(client.firebaseAuth, email.trim(), password);
        return true;
      } catch (error) {
        setErrorMessage(getReadableAuthError(error, 'Email'));
        return false;
      }
    },
    [ensureAuthSubscription],
  );

  const signUpWithEmail = useCallback(
    async (email: string, password: string) => {
      if (!firebaseEnabled) {
        setErrorMessage('Email/password sign-in is not configured on this site yet.');
        return false;
      }

      setErrorMessage(null);
      await ensureAuthSubscription();

      try {
        const { auth, client } = await loadFirebase();
        if (!client.firebaseAuth) {
          setErrorMessage('Email/password sign-in is not configured on this site yet.');
          return false;
        }
        await auth.createUserWithEmailAndPassword(client.firebaseAuth, email.trim(), password);
        return true;
      } catch (error) {
        setErrorMessage(getReadableAuthError(error, 'Email'));
        return false;
      }
    },
    [ensureAuthSubscription],
  );

  const sendPasswordReset = useCallback(async (email: string) => {
    if (!firebaseEnabled) {
      setErrorMessage('Email/password sign-in is not configured on this site yet.');
      return false;
    }

    setErrorMessage(null);

    try {
      const { auth, client } = await loadFirebase();
      if (!client.firebaseAuth) {
        setErrorMessage('Email/password sign-in is not configured on this site yet.');
        return false;
      }
      await auth.sendPasswordResetEmail(client.firebaseAuth, email.trim());
      return true;
    } catch (error) {
      setErrorMessage(getReadableAuthError(error, 'Email'));
      return false;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      session,
      isSessionLoading,
      errorMessage,
      firebaseEnabled,
      async signInWithGoogle() {
        await signInWithProvider('google', 'Google sign-in is not configured on this site yet.');
      },
      async signInWithFacebook() {
        await signInWithProvider('facebook', 'Meta sign-in is not configured on this site yet.');
      },
      async signInWithApple() {
        await signInWithProvider('apple', 'Apple sign-in is not configured on this site yet.', {
          preferRedirect: true,
        });
      },
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      async signOutUser() {
        if (!firebaseEnabled) {
          return;
        }

        setErrorMessage(null);

        try {
          const { auth, client } = await loadFirebase();
          if (!client.firebaseAuth) {
            return;
          }
          await auth.signOut(client.firebaseAuth);
          setSession(null);
          writeCachedSession(null);
        } catch (error) {
          setErrorMessage(getReadableAuthError(error));
        }
      },
      refreshSession,
    }),
    [
      errorMessage,
      isLoading,
      isSessionLoading,
      refreshSession,
      sendPasswordReset,
      session,
      signInWithEmail,
      signInWithProvider,
      signUpWithEmail,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useFirebaseAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useFirebaseAuth must be used within FirebaseAuthProvider');
  }

  return context;
}
